import React, { useState } from "react";
import { useNavigate } from "react-router";
import Breadcrumb from "../../../../../components/Breadcrumb";
import TagComp from "../../../../../components/tag";
import {
  EditIcon,
  InactiveIcon,
  TicketsDetailIcon,
} from "../../../../../icons";
import "./style.scss";
import { MoreOutlined } from "@ant-design/icons";
import AssignUnitGroup, {
  type Unit,
} from "../../../../../components/DrawerLayout/components/AssignUnitGroup";
import {
  ActivateIcon,
  DeactivateIcon,
  UnitGridIcon,
} from "../../../../../layout/Components/icons";
import { Dropdown, message } from "antd";
import ConfirmModal from "../../../../../components/ModalLayout/components/ConfirmModal";
import {
  useActivateDeactivateUnitGroup,
  useDeleteUnitGroup,
} from "../../../../../api/unitGroups";
import { isPermit } from "../../../../../utils/permissionCheck";

interface LeftSideProps {
  currentPage?: string;
  unitType: "None" | "Primary" | "Merged";
  unit: Unit;
  refetchUnit: () => void;
}

interface Section {
  title: string;
  items: { label: string; value: string }[];
}

type UnitStatus = "Active" | "Inactive";

const LeftSide: React.FC<LeftSideProps> = ({ unit, refetchUnit }) => {
  const selectedUnitData = {
    record: unit,
  };
  const [modalLoading, setModalLoading] = useState(false);

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalProps, setModalProps] = useState<{
    title: string;
    message?: string;
    onConfirm: () => void;
    confirmText?: string;
  } | null>(null);
  const [drawerMode, setDrawerMode] = useState<"add" | "edit">("add");
  const [, setSelectedUnit] = useState<Unit | null>(null);
  const [unitStatus, setUnitStatus] = useState<UnitStatus>(
    unit?.status_flg === 1 ? "Active" : "Inactive"
  );
  const navigate = useNavigate();
  const deleteMutation = useDeleteUnitGroup();
  const activateDeactivateMutation = useActivateDeactivateUnitGroup();

  const handleActivate = async () => {
    try {
      await activateDeactivateMutation.mutateAsync({
        action: "activate",
        unit_group_id:
          unit?.pikitup_unit_group_id ?? unit.record.pikitup_depot_id,
      });
      setUnitStatus("Active");
      refetchUnit();
      message.success("Unit group activated successfully!");
    } catch (error: any) {
      console.error("Activate error:", error);
      message.error(
        error.response?.data?.message ||
          "Failed to activate the unit group. Please try again."
      );
    } finally {
      setModalOpen(false);
      setModalProps(null);
    }
  };

  const handleDeactivate = async () => {
    try {
      await activateDeactivateMutation.mutateAsync({
        action: "deactivate",
        unit_group_id:
          unit?.pikitup_unit_group_id ?? unit.record.pikitup_depot_id,
      });
      setUnitStatus("Inactive");
      refetchUnit();
      message.success("Unit group deactivated successfully!");
    } catch (error: any) {
      console.error("Deactivate error:", error);
      message.error(
        error.response?.data?.message ||
          "Failed to deactivate the unit group. Please try again."
      );
    } finally {
      setModalOpen(false);
      setModalProps(null);
    }
  };

  const handleDelete = async (unitId: number) => {
    try {
      await deleteMutation.mutateAsync({
        action: "delete",
        unit_group_id: unitId,
      });
      navigate("/unit");
      refetchUnit();
      message.success("Unit group deleted successfully");
    } catch (error: any) {
      message.error(
        error?.response?.data?.message || "Failed to delete unit group"
      );
      console.error("Delete error:", error);
    }
  };

  const breadcrumbItems = [
    {
      title: (
        <span
          onClick={() => navigate("/unit")}
          className="breadcrumb-first flex gap-1"
        >
          <UnitGridIcon /> Units
        </span>
      ),
    },
    {
      title: (
        <span className="breadcrumb-second flex gap-1">
          <TicketsDetailIcon /> {`${unit?.reference_no}`}
        </span>
      ),
    },
  ];

  const sections: Section[] = [
    {
      title: "Information",
      items: [
        {
          label: "Depot",
          value: `${unit.depot_name}`,
        },
        { label: "Created on", value: unit?.created_date || "N/A" },
      ],
    },
  ];

  const descriptionData = {
    title: "Description",
    description: unit?.unit_group_desc || "No description provided",
  };

  const statusClassMap: Record<UnitStatus, string> = {
    Active: "status-pill status-active",
    Inactive: "status-pill status-inactive",
  };

  return (
    <>
      <div className="left-details-frame">
        <div className="header-wrapper flex flex-col gap-4">
          <Breadcrumb items={breadcrumbItems} />

          <div className="ticket-heading-wrapper flex gap-3 items-center">
            <h3 className="heading">{`${unit?.unit_group_name}`}</h3>

            <div className="tag-wrapper flex gap-3 items-center">
              <TagComp className="ant-tag-ticket">{`${unit?.reference_no}`}</TagComp>
              <TagComp className={statusClassMap[unitStatus]}>
                {unitStatus}
              </TagComp>
            </div>
            {isPermit(4, "edit") && (
              <div className="flex gap-2 ml-auto">
                <span>
                  <EditIcon
                    style={{
                      minWidth: "20px",
                      height: "20px",
                      cursor: "pointer",
                    }}
                    onClick={() => {
                      setDrawerOpen(true);
                      setDrawerMode("edit");
                      setSelectedUnit(unit);
                    }}
                  />
                </span>
                {}
                <Dropdown
                  menu={{
                    onClick: ({ key }) => {
                      console.debug("menu click", key, unit?.id);
                      if (key === "edit") {
                        setDrawerOpen(true);
                        setDrawerMode("edit");
                        setSelectedUnit(unit);
                        return;
                      }
                      if (key === "view") {
                        if (unit) navigate(`/unit/d/${unit.id}`);
                        return;
                      }
                      if (key === "status") {
                        setModalProps({
                          title:
                            unitStatus === "Active"
                              ? "Confirm Deactivate"
                              : "Confirm Activate",
                          message: `Are you sure you want to ${
                            unitStatus === "Active" ? "deactivate" : "activate"
                          } ${unit?.name ?? "this unit"}?`,
                          onConfirm:
                            unitStatus === "Active"
                              ? handleDeactivate
                              : handleActivate,
                          confirmText:
                            unitStatus === "Active" ? "Deactivate" : "Activate",
                        });
                        setModalOpen(true);
                        return;
                      }
                      if (key === "delete") {
                        const unitId = Number(
                          unit?.pikitup_unit_group_id ??
                            unit.record.pikitup_depot_id
                        );
                        setModalProps({
                          title: "Confirm Delete",
                          message: `Are you sure you want to delete ${
                            unit?.name ?? "this unit"
                          }? This action cannot be undone.`,
                          onConfirm: () => handleDelete(unitId),
                          confirmText: "Delete",
                        });
                        setModalOpen(true);
                      }
                    },
                    items: [
                      { icon: <EditIcon />, key: "edit", label: "Edit Unit" },
                      {
                        icon: <InactiveIcon />,
                        key: "delete",
                        danger: true,
                        label: "Remove Unit",
                      },
                      {
                        icon:
                          unitStatus === "Active" ? (
                            <span className="ml-2">
                              <DeactivateIcon />
                            </span>
                          ) : (
                            <span className="ml-2">
                              <ActivateIcon />
                            </span>
                          ),
                        key: "status",
                        label:
                          unitStatus === "Active"
                            ? "Deactivate"
                            : "Activate unit",
                      },
                    ],
                  }}
                  trigger={["click"]}
                >
                  <MoreOutlined
                    style={{
                      fontSize: "22px",
                      cursor: "pointer",
                      minWidth: "20px",
                    }}
                  />
                </Dropdown>
              </div>
            )}
          </div>

          <div className="details-wrapper flex flex-col gap-6 mt-6">
            <div className="unit-description-card mt-4">
              <h4 className="details-card-heading ">{descriptionData.title}</h4>
              <p className="description ">{descriptionData.description}</p>
            </div>

            {sections.map((section, index) => (
              <div
                key={index}
                className="section-card bg-gray-50 p-4 rounded-md shadow"
              >
                <h4 className="details-card-heading ">{section.title}</h4>
                <div className=" grid grid-cols-3 gap-4 ">
                  {section.items.map((item, idx) => (
                    <div key={idx} className="flex flex-col">
                      <span className="text-sm text-gray-500">
                        {item.label}
                      </span>
                      <span className="font-medium whitespace-pre-line">
                        {item.value}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
        <AssignUnitGroup
          refetchUnit={refetchUnit}
          open={drawerOpen}
          setOpen={setDrawerOpen}
          title={drawerMode === "edit" ? "Edit unit group" : "Add unit group"}
          unit={selectedUnitData as any}
          mode={drawerMode}
        />

        {modalOpen && (
          <ConfirmModal
            loading={modalLoading}
            open={modalOpen}
            title={modalProps?.title ?? ""}
            message={modalProps?.message}
            confirmText={modalProps?.confirmText}
            onClose={() => {
              if (!modalLoading) {
                setModalOpen(false);
                setModalProps(null);
              }
            }}
            onConfirm={async () => {
              if (!modalProps?.onConfirm) return;

              setModalLoading(true);
              try {
                await modalProps.onConfirm();
                setModalOpen(false);
                setModalProps(null);
              } catch (error) {
                console.error(error);
                message.error("Action failed. Please try again.");
              } finally {
                setModalLoading(false);
              }
            }}
          />
        )}
      </div>
    </>
  );
};

export default LeftSide;
